import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { useCallback } from "react";
import {
  createCustomEvent,
  useFireCustomEvent,
} from "@adsk/acc-issues-core/providers/CustomEventContext";
import { deleteIssueByIdAction } from "../../exports/redux";
import { CustomEventType } from "@adsk/acc-issues-types";

type deleteIssueFnType = ({
  id,
  comment,
  onSuccess,
  onFailure,
  shouldDeleteMarkup,
}: {
  id: string;
  comment: string;
  onSuccess?: () => void;
  onFailure?: () => void;
  shouldDeleteMarkup: boolean;
}) => Promise<void>;

/**
 * Returns a function that deletes an issue.
 * @param id - the ID of the issue to delete.
 * @param comment - deletation comment.
 * @param onSuccess - a custom onSuccess handler.
 * @param onFailure - a custom onFailure handler.
 * @param shouldDeleteMarkup - controls if a markup should be deleted along side with the issue.
 */
export const useDeleteIssue = (): deleteIssueFnType => {
  const dispatch = useDispatch();
  const fireCustomEvent = useFireCustomEvent();

  return useCallback(
    async ({ id, comment, onSuccess, onFailure, shouldDeleteMarkup }) => {
      const handleSuccess = (id: string) => {
        const customEventData = createCustomEvent({
          type: CustomEventType.IssueDeleted,
          target: null,
          data: { issueIds: [id] },
        });
        if (fireCustomEvent) {
          fireCustomEvent(customEventData);
        }

        onSuccess?.();
      };

      await dispatch(
        deleteIssueByIdAction({
          id,
          comment,
          onSuccess: handleSuccess,
          onFailure,
          shouldDeleteMarkup,
        })
      );

      const customEventData = createCustomEvent({
        type: CustomEventType.IssueDeleted,
        target: null,
        data: { issueIds: [id] },
      });
      if (fireCustomEvent) {
        fireCustomEvent(customEventData);
      }
    },
    [dispatch, fireCustomEvent]
  );
};
